/** Building a new game.
 *
 * Everything with an identity is minted from the counters in state and every
 * stream is seeded from the one root seed, so two `newGame` calls with the same
 * setup produce byte-identical states (I12). Nothing here reads a clock or a
 * random source.
 */

import type {
  ArmyId, ArtifactDefId, BuildingDefId, CreatureDefId, FactionId, HeroDefId, HeroId,
  IdKind, ObjectId, PlayerId, TownId,
} from "../ids.js";
import type { ContentRegistry } from "../content/registry.js";
import type { RngStream } from "../rng/streams.js";
import { RNG_STREAMS } from "../rng/streams.js";
import { seedStream, type RngStreamState } from "../rng/pcg32.js";
import type {
  AdventureMapState, ArmySlot, ArmyState, FogState, GameState, HeroState,
  MapObjectKind, MapObjectState, PlayerKind, PlayerState, Resources, TownState,
} from "../state.js";
import { CURRENT_FORMAT_VERSION } from "../state.js";
import { createFog, revealAll } from "../map/fog.js";
import { setOccupant } from "../map/create.js";
import { entityKeys } from "../util/iter.js";
import type { Ctx } from "./ctx.js";
import {
  fullResources, makeCtx, mintArmyId, mintHeroId, mintObjectId, mintTownId,
  patchPlayer, putArmy, putFog, putHero, putObject, putTown, rollRange,
} from "./ctx.js";
import {
  SPELLBOOK_ARTIFACT, SPELLBOOK_SLOT, dailyMovement, emptyArmySlots, maxMana,
} from "./hero.js";
import {
  PLAIN_WEEK, applyWeeklyGrowth, buildingsOfFaction, fillGuild, refreshTavern,
} from "./town.js";
import { recomputeVisibility } from "./visibility.js";

// ------------------------------------------------------------------ setup

export interface HeroSetup {
  readonly def: HeroDefId;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Overrides whatever `HeroArmySource` the caller of `createHero` asked for. */
  readonly army?: readonly (ArmySlot | null)[];
}

/**
 * Where a new hero's army comes from. `createHero` takes it as an argument
 * rather than defaulting to one, because the default was the bug: a hero
 * definition's opening army is 20-30 tier-1, 5-9 tier-2 and 0-2 tier-3 - about
 * 2400 gold of troops - and hiring a hero costs 2500, so a Tavern was selling
 * an army at cost and the computer bought three of them on day two.
 *
 * `"scenario"` is the map's own opening army and belongs to a hero the
 * scenario places. `"escort"` is what a Tavern sells. `"none"` is a hero who
 * arrives alone.
 */
export type HeroArmySource = "scenario" | "escort" | "none";

export interface TownSetup {
  readonly faction: FactionId;
  readonly name?: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Overrides the default opening buildings. */
  readonly built?: readonly BuildingDefId[];
  readonly garrison?: readonly (ArmySlot | null)[];
}

export interface ObjectSetup {
  readonly kind: MapObjectKind;
  readonly def: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly owner?: PlayerId | null;
  readonly payload?: Readonly<Record<string, number | string | boolean | null>>;
  readonly guard?: readonly (ArmySlot | null)[];
}

export interface PlayerSetup {
  readonly id: PlayerId;
  readonly kind: PlayerKind;
  readonly colour: string;
  readonly faction: FactionId;
  readonly resources?: Partial<Resources>;
  readonly heroes?: readonly HeroSetup[];
  readonly towns?: readonly TownSetup[];
}

export interface NewGameSetup {
  readonly rootSeed: number;
  readonly map: AdventureMapState;
  readonly players: readonly PlayerSetup[];
  /**
   * Towns nobody starts with, in the middle of the map.
   *
   * A neutral town is a `TownState` with `owner: null` and nothing else
   * special: it sits in `state.towns` beside every other town, so the siege,
   * `captureTown`, the observation, the town screen and the AI's target
   * scoring all reach it through the machinery they already have. The
   * alternative - a map object that a hero walks over - is what this replaces,
   * and it was silent, because a `Reason` has to name an entity and there was
   * none to name.
   *
   * `garrison` and `built` are not optional in practice. A neutral town that
   * ships with neither is a free capital for whoever is nearest, which is a
   * worse opening than having no neutral towns at all; the map generator sizes
   * both by the town's distance from the closest player start and hands them
   * over in `GeneratedTown`.
   */
  readonly neutralTowns?: readonly TownSetup[];
  readonly objects?: readonly ObjectSetup[];
  /**
   * The scenario's win condition, `"<kind>"` or `"<kind>:<argument>"`.
   *
   * `"defeat_all"` unless the scenario says otherwise. A kind that takes an
   * argument may be given without one - `"acquire_artifact"` - and `newGame`
   * resolves it against the world it has just built; see
   * `resolveVictoryCondition`. Anything it cannot resolve becomes
   * `"defeat_all"` rather than a condition that can never fire.
   */
  readonly victory?: string;
  readonly contentVersion?: string;
  /** Town-screen slots a starting town begins with. */
  readonly startingSlots?: readonly string[];
  /** Debug and map-editor convenience: start with the whole map explored. */
  readonly revealAll?: boolean;
}

/**
 * The four starting purses, one per difficulty.
 *
 * The classic's difficulty is first of all the HUMAN's handicap: the rules, the
 * growth and the map are the same at every level, and what the level moves is
 * how much the player begins with (the classic's own table, Easy 30000 down to
 * Impossible 0). It is the only honest place to put a difficulty bonus,
 * because the alternative - handing the computer gold, growth or sight it did
 * not earn - is a different game rather than a harder opponent, and it makes
 * every balance number measured against that opponent worthless (I9).
 *
 * So the computer is given the Normal purse at every level, and only the human
 * player's purse moves. `newGame` reads nothing about difficulty: the scenario
 * says what each player starts with through `PlayerSetup.resources`, and the
 * host picks the row (see `startingResourcesFor` in the web app).
 *
 * The classic ships five levels against this build's four; Expert (10000) is the one
 * folded out, because Impossible without the empty purse is just another Hard.
 */
export type StartingPurseId = "easy" | "normal" | "hard" | "impossible";

export const STARTING_RESOURCES: Readonly<Record<StartingPurseId, Resources>> =
  Object.freeze({
    easy: Object.freeze({
      gold: 30000, wood: 30, ore: 30, mercury: 15, sulfur: 15, crystal: 15, gems: 15,
    }),
    normal: Object.freeze({
      gold: 20000, wood: 20, ore: 20, mercury: 10, sulfur: 10, crystal: 10, gems: 10,
    }),
    hard: Object.freeze({
      gold: 15000, wood: 15, ore: 15, mercury: 7, sulfur: 7, crystal: 7, gems: 7,
    }),
    impossible: Object.freeze({
      gold: 0, wood: 0, ore: 0, mercury: 0, sulfur: 0, crystal: 0, gems: 0,
    }),
  });

/**
 * What a player starts with when the scenario does not say: the Normal purse.
 * Every computer player is on this row at every difficulty, and so is a human
 * playing Normal.
 */
export const DEFAULT_STARTING_RESOURCES: Resources = STARTING_RESOURCES.normal;

/**
 * The buildings a starting town already has. The expansion gives every town with a Fort
 * its second-level dwelling for free (Section 12), which is why `dwelling_2` is
 * in the opening set rather than being bought on day one.
 */
export const DEFAULT_STARTING_SLOTS: readonly string[] =
  Object.freeze(["hall", "fort", "tavern", "dwelling_1", "dwelling_2"]);

/**
 * What an unowned town has when the scenario does not say.
 *
 * One dwelling short of a capital, and a Fort, which is the point: the fort is
 * what makes taking the town a siege rather than a walk-in, and a walk-in
 * neutral town next door decides the game instead of shaping it. A scenario
 * that knows how far the town sits from the nearest start - the map generator
 * does - passes `TownSetup.built` and this is never read.
 */
export const DEFAULT_NEUTRAL_SLOTS: readonly string[] =
  Object.freeze(["hall", "fort", "tavern", "dwelling_1"]);

// ---------------------------------------------------------------- victory

/**
 * The argument each win condition carries after its colon.
 *
 * `victory.condition` is one string so a map file can carry it in one field
 * (`conditionWinner`, turn.ts). What that string is allowed to say is stated
 * here, once, because two places already read it apart - the rule that decides
 * the winner and the screen that names the condition - and a third, the New
 * Game screen, now writes it.
 */
export type VictoryArgument = "none" | "town" | "hero" | "artifact" | "number";

export const VICTORY_CONDITION_KINDS: Readonly<Record<string, VictoryArgument>> =
  Object.freeze({
    defeat_all: "none",
    capture_town: "town",
    defeat_hero: "hero",
    acquire_artifact: "artifact",
    accumulate_gold: "number",
    survive: "number",
    // Three conditions a map can be won on that the survivor count would never
    // have ended by itself. `flag_all_mines` and `build_grail` take no argument
    // - the first means every mine on the map at once, the second means the
    // Grail structure standing in any town of yours - and `survive_until_day`
    // takes the absolute day number the clock runs out on, which is the one
    // condition that can end a game nobody is winning.
    flag_all_mines: "none",
    build_grail: "none",
    survive_until_day: "number",
  });

export const DEFAULT_VICTORY_CONDITION = "defeat_all";

export function parseVictoryCondition(
  condition: string,
): { readonly kind: string; readonly arg: string } {
  const sep = condition.indexOf(":");
  return sep < 0
    ? { kind: condition, arg: "" }
    : { kind: condition.slice(0, sep), arg: condition.slice(sep + 1) };
}

/**
 * Turn whatever the scenario asked for into a condition this game can actually
 * be won on.
 *
 * Everything here is a refusal to start a game that is already decided or that
 * can never be decided, and every refusal falls back to `defeat_all`:
 *
 *  - an unknown kind, which `conditionWinner` would answer `null` to for ever;
 *  - `capture_town` naming a town a living player ALREADY HOLDS. The rule gives
 *    the game to whoever owns that town, so a condition pointed at anybody's
 *    capital is a win on turn one for its owner. Until a town can begin the
 *    game unowned there is no town on a generated map this condition can
 *    legitimately name, so it is refused here rather than shipped as a trap;
 *  - `defeat_hero` or `acquire_artifact` naming something that is not on the
 *    map - the artifact case being the one a scenario is *expected* to leave
 *    blank, which is why an unargued `acquire_artifact` is resolved rather than
 *    refused;
 *  - a count that is not a positive integer.
 *
 * The fallback is not silent: `state.victory.condition` holds the resolved
 * string and the end screen names it, so what a player is told he is playing
 * for is what the kernel will actually award the game on.
 */
export function resolveVictoryCondition(c: Ctx, condition: string): string {
  const { kind, arg } = parseVictoryCondition(condition);
  const takes = VICTORY_CONDITION_KINDS[kind];
  if (takes === undefined) return DEFAULT_VICTORY_CONDITION;
  if (takes === "none") {
    // Two of the unargued kinds name something the map has to actually contain.
    // A map with no mine makes `flag_all_mines` true for everybody on turn one;
    // a content set with no Grail building makes `build_grail` true for nobody,
    // ever. Both are refused here for the same reason `capture_town` is.
    if (kind === "flag_all_mines" && !mapHasMine(c)) return DEFAULT_VICTORY_CONDITION;
    if (kind === "build_grail" && !contentHasGrail(c)) return DEFAULT_VICTORY_CONDITION;
    return kind;
  }

  if (takes === "number") {
    const n = Number.parseInt(arg, 10);
    if (!Number.isFinite(n) || n <= 0) return DEFAULT_VICTORY_CONDITION;
    return `${kind}:${String(n)}`;
  }

  if (takes === "town") {
    const t = c.s.towns[arg as TownId];
    if (t === undefined) return DEFAULT_VICTORY_CONDITION;
    const owner = t.owner === null ? null : c.s.players[t.owner];
    if (owner !== null && owner !== undefined && !owner.defeated && owner.kind !== "none") {
      return DEFAULT_VICTORY_CONDITION;
    }
    return `${kind}:${arg}`;
  }

  if (takes === "hero") {
    if (arg === "" || c.s.heroes[arg as HeroId] === undefined) return DEFAULT_VICTORY_CONDITION;
    return `${kind}:${arg}`;
  }

  const named = arg === "" ? null : c.content.artifacts.get(arg as ArtifactDefId);
  if (named !== undefined && named !== null) return `${kind}:${arg}`;
  if (arg !== "") return DEFAULT_VICTORY_CONDITION;
  const found = richestArtifactOnMap(c);
  return found === null ? DEFAULT_VICTORY_CONDITION : `${kind}:${found}`;
}

/**
 * The prize an unargued `acquire_artifact` names: the dearest artifact actually
 * lying on the map.
 *
 * Dearest rather than first because the generator puts its costly artifacts
 * behind its dangerous guards, so the hunt runs through the middle of the map
 * rather than ending at the first chest on the road out of town. Ties break on
 * the artifact id so the choice is a function of the seed like everything else.
 */
export function richestArtifactOnMap(c: Ctx): ArtifactDefId | null {
  let best: ArtifactDefId | null = null;
  let bestCost = -1;
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id as ObjectId]!;
    if (o.removed) continue;
    const named = o.payload["artifact"];
    if (typeof named !== "string") continue;
    const def = c.content.artifacts.get(named as ArtifactDefId);
    if (def === undefined) continue;
    if (def.cost > bestCost || (def.cost === bestCost && best !== null && named < (best as string))) {
      bestCost = def.cost;
      best = named as ArtifactDefId;
    }
  }
  return best;
}

/** Is there a mine anywhere for `flag_all_mines` to be a race for? */
export function mapHasMine(c: Ctx): boolean {
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id as ObjectId]!;
    if (!o.removed && o.kind === "mine") return true;
  }
  return false;
}

/**
 * Does the content set ship a Grail structure at all?
 *
 * The slot is the test rather than an id, because "the Grail" is one building
 * per faction and every one of them fills the same slot. Today nothing does,
 * so `build_grail` resolves away to `defeat_all`; the condition is spelled out
 * anyway so that shipping the building is the only thing that has to change.
 */
export function contentHasGrail(c: Ctx): boolean {
  for (const b of c.content.buildings.values()) {
    if (b.slot === GRAIL_SLOT) return true;
  }
  return false;
}

export const GRAIL_SLOT = "grail";

// ------------------------------------------------------------------- seed

export function seedAllStreams(rootSeed: number): Record<RngStream, RngStreamState> {
  const out: Partial<Record<RngStream, RngStreamState>> = {};
  for (const s of RNG_STREAMS) out[s] = seedStream(rootSeed, s);
  return out as Record<RngStream, RngStreamState>;
}

const ZERO_COUNTERS: Readonly<Record<IdKind, number>> = Object.freeze({
  hero: 0, army: 0, town: 0, object: 0, stack: 0, interaction: 0, caravan: 0,
});

// ------------------------------------------------------------------ build

export function newGame(setup: NewGameSetup, content: ContentRegistry): GameState {
  const order = setup.players.map((p) => p.id);
  const skeleton: GameState = {
    version: 0,
    formatVersion: CURRENT_FORMAT_VERSION,
    contentVersion: setup.contentVersion ?? content.contentVersion,
    rootSeed: setup.rootSeed,
    rng: seedAllStreams(setup.rootSeed),
    counters: { ...ZERO_COUNTERS },
    turn: {
      day: 1, week: 1, month: 1,
      activePlayer: order[0] ?? ("" as PlayerId),
      order,
    },
    players: {},
    heroes: {},
    armies: {},
    towns: {},
    objects: {},
    caravans: {},
    map: setup.map,
    fog: {},
    battle: null,
    pending: null,
    // Rewritten once the world exists: a condition may name something the map
    // has to be searched for. See the block below `createObject`.
    victory: { condition: DEFAULT_VICTORY_CONDITION, winner: null, over: false },
  };

  const c = makeCtx(skeleton, content);

  for (const p of setup.players) {
    const state: PlayerState = {
      id: p.id,
      kind: p.kind,
      colour: p.colour,
      faction: p.faction,
      resources: fullResources({ ...DEFAULT_STARTING_RESOURCES, ...(p.resources ?? {}) }),
      heroes: [],
      towns: [],
      defeated: false,
      townlessDays: 0,
      tavern: [],
      modifiers: [],
    };
    (c.s.players as Record<PlayerId, PlayerState>)[p.id] = state;
    putFog(c, p.id, createFog(setup.map));
  }

  // Towns before heroes so a hero placed on its home town finds it, and both
  // before objects so an object may not be laid over a town cell.
  for (const p of setup.players) {
    for (const t of p.towns ?? []) createTown(c, p.id, t, setup.startingSlots);
  }
  // Neutral towns are minted after the players' so that a capital always holds
  // the lower town id, which is what a scenario reading `towns` in order
  // expects, and before the objects so the town object laid on the same cell
  // finds its town.
  for (const t of setup.neutralTowns ?? []) createTown(c, null, t, undefined);
  for (const p of setup.players) {
    for (const h of p.heroes ?? []) createHero(c, p.id, h, "scenario");
  }
  for (const o of setup.objects ?? []) createObject(c, o);

  // The world is complete, so the condition can now be checked against it and
  // an unargued one filled in.
  c.s.victory = {
    condition: resolveVictoryCondition(c, setup.victory ?? DEFAULT_VICTORY_CONDITION),
    winner: null,
    over: false,
  };

  // Day one already has a week's worth of creatures in the dwellings.
  for (const id of entityKeys(c.s.towns)) {
    applyWeeklyGrowth(c, id as TownId, PLAIN_WEEK);
    fillGuild(c, id as TownId);
  }
  for (const p of setup.players) refreshTavern(c, p.id);

  for (const p of setup.players) {
    if (setup.revealAll === true) revealAll(c.s.fog[p.id] as FogState);
    recomputeVisibility(c, p.id);
  }

  c.s.version = 1;
  return c.s as GameState;
}

// ------------------------------------------------------------------ towns

export function startingBuildings(
  content: ContentRegistry, faction: FactionId, slots: readonly string[],
): BuildingDefId[] {
  const pool = buildingsOfFaction(content, faction as string)
    .filter((b) => slots.includes(b.slot) && b.upgradeOf === null);
  const built: BuildingDefId[] = [];
  // Fixpoint rather than a fixed list: `dwelling_2` needs `dwelling_1`, and a
  // faction that orders its prerequisites differently still resolves.
  for (let pass = 0; pass < pool.length + 1; pass++) {
    let added = false;
    for (const b of pool) {
      if (built.includes(b.id)) continue;
      if (!b.requires.every((r) => built.includes(r))) continue;
      built.push(b.id);
      added = true;
    }
    if (!added) break;
  }
  built.sort();
  return built;
}

/**
 * `owner === null` mints a neutral town. Nothing below branches on it beyond
 * the two places that have to: the garrison army takes the same null owner
 * (there is no player to bill for it), and no player's `towns` list gains an
 * entry, which is what keeps `townlessDays`, income, growth and every
 * `t.owner !== playerId` filter in the kernel correct without a special case.
 */
function createTown(
  c: Ctx, owner: PlayerId | null, setup: TownSetup, slots: readonly string[] | undefined,
): TownId {
  const id = mintTownId(c);
  const garrison = mintArmyId(c);
  const slotsOut = emptyArmySlots();
  const given = setup.garrison ?? [];
  for (let i = 0; i < slotsOut.length && i < given.length; i++) slotsOut[i] = given[i] ?? null;
  putArmy(c, { id: garrison, owner, slots: slotsOut } satisfies ArmyState);

  const fallback = slots ?? (owner === null ? DEFAULT_NEUTRAL_SLOTS : DEFAULT_STARTING_SLOTS);
  const built = setup.built !== undefined
    ? [...setup.built].sort()
    : startingBuildings(c.content, setup.faction, fallback);

  const town: TownState = {
    id,
    faction: setup.faction,
    owner,
    name: setup.name ?? (id as string),
    x: setup.x, y: setup.y, z: setup.z,
    built,
    builtThisTurn: false,
    autoBuild: false,
    available: {},
    garrison,
    visitingHero: null,
    garrisonHero: null,
    guildSpells: [],
    modifiers: [],
  };
  putTown(c, town);
  if (owner !== null) {
    const p = c.s.players[owner]!;
    patchPlayer(c, owner, { towns: [...p.towns, id].sort() });
  }
  return id;
}

// ----------------------------------------------------------------- heroes

/** Roll a hero definition's opening army; `map.generation` is the world stream. */
export function rollStartingArmy(
  c: Ctx, def: HeroDefId,
): (ArmySlot | null)[] {
  const out = emptyArmySlots();
  const hd = c.content.heroes.get(def);
  if (hd === undefined) return out;
  let at = 0;
  for (const s of hd.startingArmy) {
    if (at >= out.length) break;
    const count = rollRange(c, "map.generation", s.min, s.max);
    if (count <= 0) continue;
    out[at++] = { def: s.def, count };
  }
  return out;
}

/**
 * The escort a Tavern hero arrives with: a few of the lowest-tier creature his
 * own definition opens with, and nothing else.
 *
 * A hired hero is a commander, not an army. The band is a constant, so the
 * escort cannot grow with the buyer's treasury, with the day, or with the town
 * that sold him: across the twelve rosters 3 to 6 tier-1 creatures is 75 to 420
 * gold of troops against a 2500 gold fee - a sixth of it at worst - and under
 * half a week of the dwelling that makes them. That is the whole point. A
 * second hero is worth hiring for the second pair of boots and never for the
 * stack he carries, where the definition's opening army (20-30 tier-1, 5-9
 * tier-2, 0-2 tier-3, about 2400 gold) made the Tavern the cheapest creature
 * dwelling on the map.
 *
 * The lowest tier is chosen by reading the creature, not by taking the first
 * entry, so a hero definition that lists its army in another order still sends
 * out a peasant escort rather than the dragon at the end of the list. Ties
 * break on the creature id, so the choice is a function of content and not of
 * key order (I3). The roll comes off `hero.offer`, the stream the tavern's own
 * offers are drawn from.
 */
export const TAVERN_ESCORT_MIN = 3;
export const TAVERN_ESCORT_MAX = 6;

export function rollTavernEscort(c: Ctx, def: HeroDefId): (ArmySlot | null)[] {
  const out = emptyArmySlots();
  const hd = c.content.heroes.get(def);
  if (hd === undefined) return out;

  let pick: CreatureDefId | null = null;
  let pickTier = 0;
  for (const s of hd.startingArmy) {
    const cd = c.content.creatures.get(s.def);
    if (cd === undefined) continue;
    if (pick === null || cd.tier < pickTier
      || (cd.tier === pickTier && (s.def as string) < (pick as string))) {
      pick = s.def;
      pickTier = cd.tier;
    }
  }
  if (pick === null) return out;

  const count = rollRange(c, "hero.offer", TAVERN_ESCORT_MIN, TAVERN_ESCORT_MAX);
  if (count > 0) out[0] = { def: pick, count };
  return out;
}

/** The army a source hands over, before `HeroSetup.army` gets to override it. */
function armyFor(
  c: Ctx, def: HeroDefId, from: HeroArmySource,
): readonly (ArmySlot | null)[] {
  if (from === "scenario") return rollStartingArmy(c, def);
  if (from === "escort") return rollTavernEscort(c, def);
  return emptyArmySlots();
}

export function createHero(
  c: Ctx, owner: PlayerId, setup: HeroSetup, from: HeroArmySource,
): HeroId {
  const id = mintHeroId(c);
  const armyId = mintArmyId(c);
  const hd = c.content.heroes.get(setup.def);

  const slots = emptyArmySlots();
  const given = setup.army ?? armyFor(c, setup.def, from);
  for (let i = 0; i < slots.length && i < given.length; i++) {
    slots[i] = given[i] ?? null;
  }
  putArmy(c, { id: armyId, owner, slots } satisfies ArmyState);

  const hero: HeroState = {
    id,
    def: setup.def,
    owner,
    faction: hd?.faction ?? (c.s.players[owner]!.faction),
    x: setup.x, y: setup.y, z: setup.z,
    facing: 4,
    level: 1,
    experience: 0,
    primary: hd?.startingPrimary ?? { attack: 0, defense: 0, spellPower: 1, knowledge: 1 },
    secondary: (hd?.startingSkills ?? []).map((s) => ({ def: s.def, level: s.level })),
    movementLeft: 0,
    mana: 0,
    army: armyId,
    // Every hero is trained with the book (Section 28: a cast needs one, and
    // nothing in the game hands one out). Taking it away is done by writing
    // `null` into the slot, which `hasSpellbook` reads.
    artifacts: { [SPELLBOOK_SLOT]: SPELLBOOK_ARTIFACT },
    backpack: [],
    spellbook: hd?.startingSpell != null ? [hd.startingSpell] : [],
    modifiers: [],
    boat: null,
    inTown: null,
    visited: [],
  };
  putHero(c, hero);

  // The daily pools depend on the army and the skills, so they are filled once
  // the hero exists rather than being guessed above.
  const placed = c.s.heroes[id]!;
  putHero(c, {
    ...placed,
    movementLeft: dailyMovement(c, placed),
    mana: maxMana(c, placed),
  });

  const p = c.s.players[owner]!;
  patchPlayer(c, owner, { heroes: [...p.heroes, id].sort() });
  return id;
}

// ---------------------------------------------------------------- objects

export function createObject(c: Ctx, setup: ObjectSetup): ObjectId {
  const id = mintObjectId(c);
  let guard: ArmyId | null = null;
  if (setup.guard !== undefined) {
    guard = mintArmyId(c);
    const slots = emptyArmySlots();
    for (let i = 0; i < slots.length && i < setup.guard.length; i++) {
      slots[i] = setup.guard[i] ?? null;
    }
    putArmy(c, { id: guard, owner: null, slots } satisfies ArmyState);
  }

  const payload: Record<string, number | string | boolean | null> = {};
  for (const k of Object.keys(setup.payload ?? {}).sort()) {
    payload[k] = (setup.payload ?? {})[k] ?? null;
  }

  const o: MapObjectState = {
    id,
    kind: setup.kind,
    def: setup.def,
    x: setup.x, y: setup.y, z: setup.z,
    owner: setup.owner ?? null,
    payload,
    guard,
    visitedBy: [],
    removed: false,
  };
  putObject(c, o);
  layFootprint(c, o);
  return id;
}

/**
 * Stamp an object onto the map's occupancy plane.
 *
 * The footprint blocks; the entrance cells within it do not, which is the
 * encoding the map subsystem expects (`occupant != 0 && moveCost == 0` means
 * blocked). An object with no declared footprint occupies and is entered from
 * its own anchor cell, which is what every one-tile pickup wants.
 */
export function layFootprint(c: Ctx, o: MapObjectState): void {
  const def = c.content.mapObjects.get(o.def);
  const footprint = def?.footprint ?? [];
  const entrances = def?.entrances ?? [];
  const blocks = def?.blocksMovement ?? false;

  if (footprint.length === 0) {
    setOccupant(c.s.map, o.x, o.y, o.z, o.id, blocks && entrances.length > 0);
    return;
  }
  const isEntrance = (dx: number, dy: number): boolean =>
    entrances.some(([ex, ey]) => ex === dx && ey === dy);
  for (const [dx, dy] of footprint) {
    setOccupant(c.s.map, o.x + dx, o.y + dy, o.z, o.id, blocks && !isEntrance(dx, dy));
  }
}
